import Paper from "@mui/material/Paper";
import { useTranslation } from "react-i18next";
import { getFileExtension } from "../utils/getFileExtension";

// File types that can be natively previewed in browsers
const PREVIEWABLE_FILE_TYPES = {
  // Documents
  pdf: { component: "iframe", mimeType: "application/pdf" },
  txt: { component: "iframe", mimeType: "text/plain" },

  // Images
  jpg: { component: "img", mimeType: "image/jpeg" },
  jpeg: { component: "img", mimeType: "image/jpeg" },
  png: { component: "img", mimeType: "image/png" },
  gif: { component: "img", mimeType: "image/gif" },
  svg: { component: "img", mimeType: "image/svg+xml" },
  webp: { component: "img", mimeType: "image/webp" },
  bmp: { component: "img", mimeType: "image/bmp" },

  // Audio
  mp3: { component: "audio", mimeType: "audio/mpeg" },
  wav: { component: "audio", mimeType: "audio/wav" },
  ogg: { component: "audio", mimeType: "audio/ogg" },
  m4a: { component: "audio", mimeType: "audio/mp4" },

  // Video
  mp4: { component: "video", mimeType: "video/mp4" },
  webm: { component: "video", mimeType: "video/webm" },
  ogv: { component: "video", mimeType: "video/ogg" },
} as const;

export function isPreviewableFile(fileName: string): boolean {
  const extension = getFileExtension(fileName);
  return extension in PREVIEWABLE_FILE_TYPES;
}

function getPreviewConfig(fileName: string) {
  const extension = getFileExtension(fileName);
  return PREVIEWABLE_FILE_TYPES[
    extension as keyof typeof PREVIEWABLE_FILE_TYPES
  ];
}

interface FilePreviewProps {
  fileName: string;
  src: string;
  maxWidth?: number | string;
  maxHeight?: number | string;
}

const baseSx = { maxWidth: "100%" };

export function FilePreview({
  fileName,
  src,
  maxWidth,
  maxHeight,
}: FilePreviewProps) {
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:FilePreview",
  });

  if (!isPreviewableFile(fileName)) {
    return null;
  }

  const previewConfig = getPreviewConfig(fileName);

  switch (previewConfig.component) {
    case "iframe":
      return (
        <Paper
          component="iframe"
          variant="outlined"
          src={src}
          title={fileName}
          width="100%"
          height="100%"
          sx={{ ...baseSx, minHeight: 700 }}
        />
      );

    case "img":
      return (
        <Paper
          component="img"
          src={src}
          alt={fileName}
          variant="outlined"
          sx={{
            ...baseSx,
            height: "auto",
            display: "block",
            ...(maxWidth && { maxWidth }),
            ...(maxHeight && { maxHeight }),
            objectFit: "contain",
          }}
        />
      );

    case "audio":
      return (
        <Paper variant="outlined" sx={{ ...baseSx, p: 2 }}>
          <audio controls style={{ width: "100%" }} preload="metadata">
            <source src={src} type={previewConfig.mimeType} />
            <track kind="captions" />
            {t("audioNotSupported", "Audio not supported")}
          </audio>
        </Paper>
      );

    case "video":
      return (
        <Paper variant="outlined" sx={baseSx}>
          <video
            controls
            style={{
              width: "100%",
              height: "auto",
              ...(maxWidth && { maxWidth }),
              ...(maxHeight && { maxHeight }),
              objectFit: "contain",
            }}
            preload="metadata"
          >
            <source src={src} type={previewConfig.mimeType} />
            <track kind="captions" />
            {t("videoNotSupported", "Video not supported")}
          </video>
        </Paper>
      );

    default:
      return null;
  }
}
